import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import type { Provider as CollabProvider } from '@atlaskit/collab-provider/provider';
import type { CollabEditProvider } from '@atlaskit/editor-common/collab';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';

import getPublishedContentAppearanceProperty from './__generated__/contentAppearanceQuery.graphql.ts';
import type { contentAppearanceQuery$data } from './__generated__/contentAppearanceQuery.graphql.ts';
import type {
	ContentAppearance,
	EditorWidthMetadata,
	GraphqlAppearanceContentProperty,
	GraphqlQueryContentWithDraftAppearance,
	GraphqlQueryContentWithPublishedAppearance,
	UpdateContentAppearancePropertyBody,
} from './contentAppearanceTypes.ts';
import { ContentAppearancePropertyKey, ContentAppearanceType } from './contentAppearanceTypes.ts';

type CollabEditProviderArg =
	| CollabEditProvider
	| (CollabEditProvider & CollabProvider)
	| null
	| undefined;

export const contentAppearanceQuery: GraphQLTaggedNode = graphql`
	query contentAppearanceQuery($contentId: ID, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				appearanceDraft: properties(key: "content-appearance-draft") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
			}
		}
	}
`;

export const getContentAppearanceFromContentProperties = (
	properties: GraphqlAppearanceContentProperty['nodes'],
	key: ContentAppearancePropertyKey,
): ContentAppearance => {
	/**
	 * Auto-converted pages are served default values for content appearance from BE if a persisted property value does not exist.
	 * BE provides these default values without a version field.
	 */
	const contentAppearanceProperty = properties?.find((property) => property?.key === key);

	let appearance = ContentAppearanceType.DEFAULT;
	let version = 0;
	if (contentAppearanceProperty) {
		appearance = contentAppearanceProperty.value
			? (JSON.parse(contentAppearanceProperty.value) as ContentAppearanceType)
			: appearance;
		version = contentAppearanceProperty.version?.number || version;
	}

	return { appearance, version };
};

export const getContentAppearanceDraft = (
	content: GraphqlQueryContentWithDraftAppearance,
): ContentAppearance => {
	return getContentAppearanceFromContentProperties(
		content.appearanceDraft?.nodes ?? null,
		ContentAppearancePropertyKey.DRAFT,
	);
};

export const getContentAppearancePublished = (
	content: GraphqlQueryContentWithPublishedAppearance,
): ContentAppearance => {
	return getContentAppearanceFromContentProperties(
		content.appearancePublished?.nodes ?? null,
		ContentAppearancePropertyKey.PUBLISHED,
	);
};

export const updateContentAppearancePropertyWithRetry = async (
	updateContentAppearanceProperty: (params: UpdateContentAppearancePropertyBody) => Promise<void>,
	params: UpdateContentAppearancePropertyBody,
	collabEditProvider?: CollabEditProviderArg,
): Promise<UpdateContentAppearancePropertyBody> => {
	try {
		await updateContentAppearanceProperty(params);
		updateAppearanceForCollab(collabEditProvider, params);
		return params;
	} catch (error) {
		// If it was an HTTP 409 conflict, retry once with the latest version number + 1
		if (isStatusCodeError(error, '409')) {
			const appearanceProperty = await fetchContentAppearanceProperty(
				params.contentId,
				params.propertyKey,
				'network-only',
			);
			if (appearanceProperty.version) {
				params = prepareNewParams(params, appearanceProperty);
				await updateContentAppearanceProperty(params);
				updateAppearanceForCollab(collabEditProvider, params);
				return params;
			}
		}
		throw error;
	}
};

export const prepareNewParams = (
	params: UpdateContentAppearancePropertyBody,
	appearanceProperty: ContentAppearance,
): UpdateContentAppearancePropertyBody => {
	params.versionNumber = appearanceProperty.version + 1;
	if (params.propertyValue !== appearanceProperty.appearance) {
		params.propertyValue =
			appearanceProperty.appearance === ContentAppearanceType.DEFAULT
				? ContentAppearanceType.FULL_WIDTH
				: ContentAppearanceType.DEFAULT;
	}
	return params;
};

export function getContentAppearanceFromQueryData(
	data: contentAppearanceQuery$data | undefined,
	propertyKey: ContentAppearancePropertyKey,
): ContentAppearance {
	const content = data?.content?.nodes?.[0];

	if (content) {
		return propertyKey === ContentAppearancePropertyKey.PUBLISHED
			? getContentAppearancePublished(
					content as unknown as GraphqlQueryContentWithPublishedAppearance,
				)
			: getContentAppearanceDraft(content);
	}
	return getDefaultContentAppearance();
}

export const fetchContentAppearanceProperty = async (
	contentId: string,
	propertyKey: ContentAppearancePropertyKey,
	fetchPolicy: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache' = 'cache-first',
): Promise<ContentAppearance> => {
	// This dummy variable forces "network-only" requests to bypass the cache.
	// A bug in Apollo sometimes returns cached data even with "network-only".
	// See: https://pug.jira-dev.com/wiki/spaces/~683304838/pages/5971970200/Apollo+Client+-+weird+refetch+behavior
	const dummy = fetchPolicy === 'network-only' ? Date.now() : undefined;

	let contentAppearanceDraftProperty;

	if (propertyKey === ContentAppearancePropertyKey.PUBLISHED) {
		contentAppearanceDraftProperty = await queryInterop<contentAppearanceQuery$data>({
			query: getPublishedContentAppearanceProperty,
			variables: {
				contentId,
				status: ['current', 'draft'],
				dummy,
			},
			fetchPolicy,
			context: { single: 'true' },
		});
	} else {
		contentAppearanceDraftProperty = await queryInterop<contentAppearanceQuery$data>({
			query: contentAppearanceQuery,
			variables: {
				contentId,
				status: ['current', 'draft'],
				dummy,
			},
			fetchPolicy,
			context: { single: 'true' },
		});
	}

	return getContentAppearanceFromQueryData(contentAppearanceDraftProperty?.data, propertyKey);
};

export const getDefaultContentAppearance = (): ContentAppearance => {
	return { appearance: ContentAppearanceType.DEFAULT, version: 0 };
};

export const UPDATE_APPEARANCE_VIA_COLLAB_PROVIDER_KEY = 'editorAppearance';

export const updateAppearanceForCollab = (
	collabEditProvider: CollabEditProviderArg,
	params: UpdateContentAppearancePropertyBody,
): void => {
	if (!collabEditProvider) {
		return;
	}
	// NCS provider has setMetadata/getMetadata
	if ('setMetadata' in collabEditProvider && typeof collabEditProvider.setMetadata === 'function') {
		const editorWidth: EditorWidthMetadata = {
			value: params.propertyValue,
			version: params.versionNumber,
		};
		const metadata = {
			...collabEditProvider.getMetadata(),
			metadataSource: 'client',
			editorWidth,
		};
		// @ts-expect-error Metadata type restricts values to primitives but editorWidth is a nested object at runtime
		collabEditProvider.setMetadata(metadata);
	} else {
		// Synchrony provider uses sendMessage
		collabEditProvider.sendMessage({
			type: UPDATE_APPEARANCE_VIA_COLLAB_PROVIDER_KEY,
			appearance: params.propertyValue,
			version: params.versionNumber,
		} as Parameters<typeof collabEditProvider.sendMessage>[0]);
	}
};

export const updateContentAppearancePropertyAfterOfflineMode = async (
	appearanceProperty: ContentAppearance,
	params: UpdateContentAppearancePropertyBody,
	updateContentAppearanceProperty: (params: UpdateContentAppearancePropertyBody) => Promise<void>,
	collabEditProvider: CollabEditProviderArg,
): Promise<void> => {
	const newParams = {
		...params,
		propertyValue: appearanceProperty.appearance,
		versionNumber: appearanceProperty.version + 1,
	};

	if (appearanceProperty.version) {
		await updateContentAppearanceProperty(newParams);
		updateAppearanceForCollab(collabEditProvider, newParams);
	}
};
